import {
  ApiV2Error,
  createUpload,
  finalizeUpload,
  getUpload,
  UploadFileEntry,
  UploadMissingFile,
} from './apiV2';

/**
 * A local folder, as the upload sends it: every file with its path relative to
 * the folder, minus what never belongs in a deploy.
 *
 * Paths are relative to the folder that was picked or dropped, the same as
 * `b4a containers deploy` run inside it: dropping `my-app/` uploads its
 * contents, not a `my-app` directory.
 */

export interface PickedFile {
  path: string;
  file: File;
}

export interface PickedFolder {
  name: string;
  files: PickedFile[];
  ignoredCount: number;
  totalBytes: number;
}

// Same limits the API enforces, checked before anything is sent.
export const UPLOAD_LIMITS = {
  maxFiles: 20000,
  maxTotalBytes: 500 * 1024 * 1024,
  maxFileBytes: 100 * 1024 * 1024,
};

// Never part of a deploy: VCS data, installed dependencies, secrets, OS junk.
// The build installs dependencies itself; environment variables are set on the
// app, never shipped in a file.
const IGNORED_DIRECTORIES = new Set(['.git', 'node_modules', '__pycache__', '.venv']);
const IGNORED_FILES = new Set(['.DS_Store', 'Thumbs.db']);

export const isIgnoredPath = (path: string): boolean => {
  const segments = path.split('/');
  const name = segments[segments.length - 1];

  return (
    segments.slice(0, -1).some((segment) => IGNORED_DIRECTORIES.has(segment)) ||
    IGNORED_FILES.has(name) ||
    name === '.env' ||
    name.startsWith('.env.')
  );
};

const stripCommonRoot = (entries: PickedFile[]): { name: string; files: PickedFile[] } => {
  const first = entries[0]?.path.split('/')[0];
  const allUnderOneFolder =
    !!first && entries.every((entry) => entry.path.startsWith(`${first}/`));

  if (!allUnderOneFolder) {
    return { name: 'Selected files', files: entries };
  }

  return {
    name: first,
    files: entries.map((entry) => ({ ...entry, path: entry.path.slice(first.length + 1) })),
  };
};

const toFolder = (entries: PickedFile[]): PickedFolder => {
  const { name, files } = stripCommonRoot(entries);
  const kept = files.filter((entry) => !isIgnoredPath(entry.path));

  return {
    name,
    files: kept,
    ignoredCount: files.length - kept.length,
    totalBytes: kept.reduce((sum, entry) => sum + entry.file.size, 0),
  };
};

/** From `<input type="file" webkitdirectory>`: paths come in webkitRelativePath. */
export const folderFromInput = (fileList: FileList): PickedFolder =>
  toFolder(
    Array.from(fileList).map((file) => ({
      path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      file,
    }))
  );

// readEntries hands directories over in pages (100 at a time in Chrome): keep
// reading until a page comes back empty.
const readAllEntries = async (directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> => {
  const reader = directory.createReader();
  const all: FileSystemEntry[] = [];
  for (;;) {
    const page = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject)
    );
    if (!page.length) {
      return all;
    }
    all.push(...page);
  }
};

const walkEntry = async (entry: FileSystemEntry, prefix: string, out: PickedFile[]) => {
  const path = prefix ? `${prefix}/${entry.name}` : entry.name;

  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject)
    );
    out.push({ path, file });
    return;
  }

  // Not descended into at all: node_modules alone can be tens of thousands of
  // entries that would only be thrown away.
  if (entry.isDirectory && !IGNORED_DIRECTORIES.has(entry.name)) {
    for (const child of await readAllEntries(entry as FileSystemDirectoryEntry)) {
      await walkEntry(child, path, out);
    }
  }
};

/** From a drop: folders are walked through the FileSystem entry API. */
export const folderFromDrop = async (dataTransfer: DataTransfer): Promise<PickedFolder> => {
  // Read synchronously, before any await: the items are only valid during
  // the drop event itself.
  const entries = Array.from(dataTransfer.items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => !!entry);

  const files: PickedFile[] = [];
  for (const entry of entries) {
    await walkEntry(entry, '', files);
  }

  return toFolder(files);
};

/** What is wrong with a folder before sending it, or null. */
export const folderProblem = (folder: PickedFolder): string | null => {
  if (folder.files.length === 0) {
    return 'This folder has no files to upload.';
  }
  if (folder.files.length > UPLOAD_LIMITS.maxFiles) {
    return `This folder has ${folder.files.length.toLocaleString()} files; the limit is ${UPLOAD_LIMITS.maxFiles.toLocaleString()}. Leave build output and dependencies out.`;
  }
  const big = folder.files.find((entry) => entry.file.size > UPLOAD_LIMITS.maxFileBytes);
  if (big) {
    return `${big.path} is larger than 100 MB. Keep large assets in storage or a volume instead of the image.`;
  }
  if (folder.totalBytes > UPLOAD_LIMITS.maxTotalBytes) {
    return 'This folder is larger than 500 MB. Leave build output and dependencies out.';
  }
  return null;
};

const hex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const mapLimit = async <T>(items: T[], limit: number, fn: (item: T) => Promise<void>) => {
  let next = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (next < items.length) {
        await fn(items[next++]);
      }
    })
  );
};

export type UploadPhase = 'hashing' | 'sending' | 'finalizing';

export interface UploadProgress {
  phase: UploadPhase;
  /** 0..1 */
  fraction: number;
  /** Files the account did not have yet: the only ones sent. */
  filesToSend?: number;
}

export interface UploadedFolder {
  uploadId: string;
  manifestSha256: string;
  fileCount: number;
  sentCount: number;
  paths: string[];
}

const putFile = async (missing: UploadMissingFile, file: File) => {
  const response = await fetch(missing.upload.url, {
    method: 'PUT',
    headers: missing.upload.headers,
    body: file,
  });
  if (!response.ok) {
    throw new ApiV2Error(response.status, 'UPLOAD_FAILED', 'A file could not be sent.');
  }
};

// Script names that are run as ./name and ship without a shebang check.
const EXECUTABLE_NAMES = new Set(['gradlew', 'mvnw']);

/**
 * The browser can't read a file's permissions, so the executable bit is
 * inferred: a script (starts with "#!"), a *.sh file, or a build wrapper.
 * Without it, `./entrypoint.sh` or `./gradlew` fails in the build with
 * "permission denied", where the same folder from git or the CLI works.
 */
export const inferMode = (path: string, bytes: ArrayBuffer): number => {
  const name = path.split('/').pop() || '';
  const head = new Uint8Array(bytes, 0, Math.min(2, bytes.byteLength));
  const isScript = head.length === 2 && head[0] === 0x23 && head[1] === 0x21;
  return isScript || name.endsWith('.sh') || EXECUTABLE_NAMES.has(name) ? 0o755 : 0o644;
};

/**
 * Hashes the folder, sends only what the account does not have yet, and
 * finalizes. One retry round after a failure: GET /v1/uploads/{id} says what
 * is still missing, with fresh links.
 */
export const uploadFolder = async (
  folder: PickedFolder,
  onProgress: (progress: UploadProgress) => void,
  // New code for an existing app, rather than the code of a new one.
  appId?: string
): Promise<UploadedFolder> => {
  const entries: (UploadFileEntry & { file: File })[] = [];
  let hashed = 0;

  onProgress({ phase: 'hashing', fraction: 0 });
  await mapLimit(folder.files, 4, async ({ path, file }) => {
    const bytes = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    entries.push({ path, sha256: hex(digest), size: file.size, mode: inferMode(path, bytes), file });
    hashed++;
    onProgress({ phase: 'hashing', fraction: hashed / folder.files.length });
  });

  const upload = await createUpload(
    entries.map(({ file, ...entry }) => entry),
    appId
  );
  const bySha = new Map(entries.map((entry) => [entry.sha256, entry.file]));

  const send = async (missing: UploadMissingFile[]) => {
    const totalBytes = missing.reduce((sum, item) => sum + item.size, 0) || 1;
    let sentBytes = 0;
    onProgress({ phase: 'sending', fraction: 0, filesToSend: missing.length });
    await mapLimit(missing, 6, async (item) => {
      await putFile(item, bySha.get(item.sha256)!);
      sentBytes += item.size;
      onProgress({ phase: 'sending', fraction: sentBytes / totalBytes, filesToSend: missing.length });
    });
  };

  try {
    await send(upload.missing);
  } catch (e) {
    const retry = await getUpload(upload.uploadId);
    await send(retry.missing);
  }

  onProgress({ phase: 'finalizing', fraction: 1, filesToSend: upload.missing.length });
  const ready = await finalizeUpload(upload.uploadId);

  return {
    uploadId: ready.uploadId,
    manifestSha256: ready.manifestSha256,
    fileCount: ready.fileCount,
    sentCount: upload.missing.length,
    paths: entries.map((entry) => entry.path),
  };
};
